/**
 * IT Admin → a person's profile (5 Oct): what ITSM records about them.
 *
 * The person card (8 Oct: Identity, Contact, Work, Ivanti, with Copy on the
 * user ID, email and phones), access by system (current picture, then every request
 * with its link), open incidents and requests, the requests that name them,
 * and — honestly — what the HelpDesk cannot see yet and where it will come
 * from. Opening it is logged on the server (who looked up whom).
 */

import { Alert, Badge, Card, DescriptionList, Typography } from "@norican/react";
import { Link, useParams } from "react-router";

import { ErrorState, LoadingState, SectionCard, useDateFormatter } from "@/components/common";
import { TicketStatusBadge } from "@/features/tickets";
import { ViewAsButton } from "@/features/view-as";
import { useAnswerLabels } from "@/hooks";
import { useT } from "@/i18n";
import { ALL_ANSWER_KEYS, profileSummary } from "@/lib/access-profile";
import type {
  NamedOnTicketSummary,
  ProfileAccessRequest,
  ProfilePerson,
  ProfileSystem,
  ProfileTickets,
} from "@/lib/api";

import { AccessGlance } from "./access-glance";
import { useItAdminPerson } from "./api";
import { CopyValue } from "./copy-value";
import { type PersonRow, personSections } from "./person-sections";

export function PersonProfilePage() {
  const { t } = useT("tickets");
  const { employeeId = "" } = useParams();
  const profile = useItAdminPerson(employeeId);

  if (profile.isPending) {
    return <LoadingState label={t("itAdmin.profile.loading")} />;
  }
  if (profile.isError) {
    return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  }
  const data = profile.data;
  const person = data.person;
  return (
    <div className="flex flex-col gap-6">
      <Link to="/it-admin" className="text-fg-muted underline underline-offset-2">
        {t("itAdmin.profile.back")}
      </Link>
      {data.complete === false ? (
        <Alert tone="warning">{t("itAdmin.profile.partial")}</Alert>
      ) : null}
      {/* 21 Oct: the access at a glance (hidden while the switch is off). */}
      <AccessGlance employeeId={employeeId} person={person} />
      <SectionCard title={person.name}>
        <PersonCard person={person} />
        <ViewAsButton employeeId={employeeId} className="mt-4" />
      </SectionCard>

      <SectionCard title={t("itAdmin.profile.accessTitle")}>
        {(data.access ?? []).length === 0 ? (
          <Typography variant="body-sm" tone="muted">
            {t("itAdmin.profile.noAccess")}
          </Typography>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-4 p-0">
            {(data.access ?? []).map((system) => (
              <li key={system.system}>
                <SystemCard system={system} />
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <div className="grid gap-6 md:grid-cols-2">
        <TicketsCard title={t("itAdmin.profile.incidents")} tickets={data.incidents} />
        <TicketsCard title={t("itAdmin.profile.requests")} tickets={data.requests} />
      </div>

      <NamedOnCard items={data.named_on ?? []} />

      <SectionCard title={t("itAdmin.profile.notVisibleTitle")}>
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {(data.not_visible ?? []).map((entry) => (
            <li key={entry.key}>
              <Typography variant="body-sm">
                {t(`itAdmin.notVisible.${entry.key}` as never)}
                {" — "}
                <span className="text-fg-muted">
                  {t(`itAdmin.source.${entry.source}` as never)}
                </span>
              </Typography>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

function PersonCard({ person }: { person: ProfilePerson }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const none = t("itAdmin.profile.unknown");
  const sections = personSections(person, {
    flag: (value) => t(value ? "itAdmin.person.yes" : "itAdmin.person.no"),
    date: (iso) => formatDate(iso),
    leaver: (flags) =>
      flags.length === 0
        ? t("itAdmin.person.leaverNone")
        : flags.map((flag) => t(`itAdmin.person.leaverFlag.${flag}`)).join(", "),
    signIn: (external, internal) => {
      const ways = [
        external ? t("itAdmin.person.signIn.sso") : null,
        internal ? t("itAdmin.person.signIn.password") : null,
      ].filter((way): way is string => way !== null);
      return ways.length === 0 ? t("itAdmin.person.signIn.none") : ways.join(", ");
    },
  });
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {sections.map((section) => (
        <section key={section.key} className="flex flex-col gap-2">
          <Typography as="h3" variant="label">
            {t(`itAdmin.person.section.${section.key}`)}
          </Typography>
          <DescriptionList
            columns={1}
            items={section.rows.map((row) => {
              const label = t(`itAdmin.person.field.${row.key}`);
              return {
                label,
                value: <PersonValue row={row} label={label} none={none} />,
              };
            })}
          />
        </section>
      ))}
    </div>
  );
}

function PersonValue({ row, label, none }: { row: PersonRow; label: string; none: string }) {
  const { t } = useT("tickets");
  if (row.value === null) {
    return <span className="text-fg-muted">{none}</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1 break-all">
      {row.href ? (
        <Link to={row.href} className="underline underline-offset-2">
          {row.value}
        </Link>
      ) : (
        row.value
      )}
      {row.copy ? (
        <CopyValue value={row.value} label={t("itAdmin.person.copy", { label })} />
      ) : null}
    </span>
  );
}

function SystemCard({ system }: { system: ProfileSystem }) {
  const { t } = useT("tickets");
  const labels = useAnswerLabels();
  const summary = profileSummary(system.current, labels);
  return (
    <Card padding="md" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Typography as="h4" variant="label">
          {system.system}
        </Typography>
        {system.removed ? (
          <Badge tone="neutral" variant="subtle" size="xs">
            {t("itAdmin.profile.removed")}
          </Badge>
        ) : system.current != null ? (
          <Badge tone="success" variant="subtle" size="xs">
            {t("itAdmin.profile.current")}
          </Badge>
        ) : null}
      </div>
      {summary === "" ? null : <Typography variant="body-sm">{summary}</Typography>}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {(system.history ?? []).map((request) => (
          <li key={request.ticket_id}>
            <RequestRow request={request} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function RequestRow({ request }: { request: ProfileAccessRequest }) {
  const { t } = useT("tickets");
  const labels = useAnswerLabels();
  const { formatDate } = useDateFormatter();
  const details = profileSummary(request, labels, ALL_ANSWER_KEYS);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to={`/tickets/${encodeURIComponent(request.ticket_id)}`}
          className="underline underline-offset-2"
        >
          {request.offering} {request.number}
        </Link>
        <TicketStatusBadge status={request.status} kind="SERVICE_REQUEST" />
        <Badge tone={request.action === "remove" ? "neutral" : "accent"} variant="subtle" size="xs">
          {t(`itAdmin.action.${request.action}`)}
        </Badge>
        {request.created_at ? (
          <Typography variant="caption" tone="muted">
            {formatDate(request.created_at)}
          </Typography>
        ) : null}
        <Typography variant="caption" tone="muted">
          {t(`itAdmin.via.${request.via}`)}
        </Typography>
      </div>
      {details === "" ? null : (
        <Typography variant="caption" tone="muted">
          {details}
        </Typography>
      )}
    </div>
  );
}

function TicketsCard({ title, tickets }: { title: string; tickets: ProfileTickets | undefined }) {
  const { t } = useT("tickets");
  const items = tickets?.items ?? [];
  return (
    <SectionCard title={`${title} (${tickets?.total ?? 0})`}>
      {items.length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.profile.noneOpen")}
        </Typography>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((ticket) => (
            <li key={ticket.ticket_id} className="flex flex-wrap items-center gap-2">
              <Link to={ticket.url_path} className="underline underline-offset-2">
                {ticket.ticket_number}
              </Link>
              <Typography variant="body-sm">{ticket.subject.text}</Typography>
              <TicketStatusBadge status={ticket.status} kind={ticket.kind} />
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

function NamedOnCard({ items }: { items: NamedOnTicketSummary[] }) {
  const { t } = useT("tickets");
  return (
    <SectionCard title={t("itAdmin.profile.namedOn")}>
      {items.length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.profile.noNamedOn")}
        </Typography>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((ticket) => (
            <li key={ticket.ticket_id} className="flex flex-wrap items-center gap-2">
              <Link to={ticket.url_path} className="underline underline-offset-2">
                {ticket.ticket_number}
              </Link>
              <Typography variant="body-sm">{ticket.subject.text}</Typography>
              <Typography variant="caption" tone="muted">
                {ticket.named_as}
              </Typography>
              <TicketStatusBadge status={ticket.status} kind={ticket.kind} />
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
